import { useState } from "react";
import { motion } from "framer-motion";

const services = [
  { n: "01", label: "Reels & Contenido para redes" },
  { n: "02", label: "Motion Graphics" },
  { n: "03", label: "Dirección de Fotografía" },
  { n: "04", label: "Documental & Retrato de Marca" },
  { n: "05", label: "Post-producción & Color" },
];

const pillars = [
  {
    key: "mirada",
    title: "Mirada Creativa",
    body: "Perspectiva editorial y honesta. Buscamos el encuadre que otros no ven y lo elevamos a lenguaje de marca.",
  },
  {
    key: "produccion",
    title: "Producción con Intención",
    body: "Cada plan de rodaje responde a una idea. Sin ruido, sin excesos: sólo lo que sirve a la historia.",
  },
  {
    key: "experiencias",
    title: "Experiencias Memorables",
    body: "Trabajamos para que la pieza no se vea; se recuerde. Un detalle, un ritmo, una emoción que se queda.",
  },
  {
    key: "calidad",
    title: "Calidad Visual Contemporánea",
    body: "Estética actual, técnica precisa. Formato vertical, color trabajado y acabado que compite con lo mejor.",
  },
];

export const Nosotros = () => {
  const [active, setActive] = useState("mirada");
  const activePillar = pillars.find((p) => p.key === active);

  return (
    <section id="servicios" data-testid="nosotros-section" className="relative">
      {/* Header row */}
      <div className="border-b border-divider">
        <div className="grid grid-cols-12 gap-6 px-6 md:px-10 py-10 md:py-16">
          <div className="col-span-12 md:col-span-3 font-mono text-[10px] uppercase tracking-[0.25em] text-rust">
            [03] Servicios
          </div>
          <div className="col-span-12 md:col-span-9">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ type: "spring", stiffness: 100, damping: 15 }}
              className="font-serif text-2xl md:text-4xl lg:text-5xl leading-[1.2] tracking-[-0.01em] text-white"
              data-testid="nosotros-tagline"
            >
              Asombro es una productora creativa que convierte{" "}
              <span className="text-rust">ideas, marcas y momentos</span> en
              experiencias visuales memorables.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Services enumerated */}
      <div className="grid grid-cols-12 border-b border-divider">
        <div className="col-span-12 md:col-span-3 border-b md:border-b-0 md:border-r border-divider px-6 md:px-10 py-10 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
          Servicios
        </div>
        <div className="col-span-12 md:col-span-9">
          <ul>
            {services.map((s, i) => (
              <li
                key={s.n}
                data-testid={`service-item-${s.n}`}
                className={`group grid grid-cols-12 items-center px-6 md:px-10 py-6 md:py-7 border-divider ${
                  i !== services.length - 1 ? "border-b" : ""
                }`}
              >
                <span className="col-span-2 md:col-span-1 font-mono text-[11px] uppercase tracking-[0.25em] text-rust">
                  [{s.n}]
                </span>
                <span className="col-span-10 md:col-span-9 font-display uppercase text-white text-xl md:text-3xl lg:text-4xl leading-tight tracking-[-0.01em] group-hover:text-rust transition-colors duration-300">
                  {s.label}
                </span>
                <span className="hidden md:block col-span-2 text-right font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 group-hover:text-rust transition-colors duration-300">
                  ↗
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Pillars interactive */}
      <div className="grid grid-cols-12 border-b border-divider">
        <div className="col-span-12 md:col-span-3 border-b md:border-b-0 md:border-r border-divider px-6 md:px-10 py-10 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
          Pilares
        </div>
        <div className="col-span-12 md:col-span-9 grid grid-cols-1 md:grid-cols-2">
          {/* Tabs */}
          <div className="border-b md:border-b-0 md:border-r border-divider">
            {pillars.map((p) => {
              const isActive = active === p.key;
              return (
                <button
                  type="button"
                  key={p.key}
                  onMouseEnter={() => setActive(p.key)}
                  onFocus={() => setActive(p.key)}
                  onClick={() => setActive(p.key)}
                  data-testid={`pillar-tab-${p.key}`}
                  className={`w-full flex items-center justify-between text-left px-6 md:px-10 py-6 border-b border-divider last:border-b-0 transition-colors ${
                    isActive ? "bg-black" : "bg-black hover:bg-white/[0.02]"
                  }`}
                >
                  <span
                    className={`font-display uppercase text-lg md:text-2xl tracking-[-0.01em] ${
                      isActive ? "text-rust" : "text-white"
                    }`}
                  >
                    {p.title}
                  </span>
                  <span
                    className={`font-mono text-[11px] uppercase tracking-[0.25em] ${
                      isActive ? "text-rust" : "text-white/40"
                    }`}
                  >
                    {isActive ? "●" : "○"}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Description panel */}
          <div className="relative min-h-[280px] flex items-center px-6 md:px-10 py-10">
            <motion.div
              key={activePillar?.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 140, damping: 18 }}
              data-testid="pillar-description"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-rust mb-4">
                Pilar activo
              </div>
              <div className="font-display uppercase text-2xl md:text-4xl leading-tight tracking-[-0.01em] mb-6 text-white">
                {activePillar?.title}
              </div>
              <p className="font-serif text-lg md:text-xl leading-relaxed text-white/80 max-w-[46ch]">
                {activePillar?.body}
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
